import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Select, { InputActionMeta, MultiValue, StylesConfig } from 'react-select';
import toaster from '../../../../Utils/toaster';
import './MeetingRoomsSection.css';
import {BulkVosSettingsTab} from './BulkVosSettingsTab'
import '../../bulk-vos/bulkVosSettings.css'

const API_URL = process.env.REACT_APP_API_URL;

interface AllowedStaffEmailDomain {
  domain: string;
  allowSubdomains: boolean;
}

interface VosApprovalParticipant {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ParticipantOption {
  value: string;
  label: string;
  email: string;
}

interface VosAttendanceSettingsDto {
  checkInGpsProximityEnabled: boolean;
  checkInGpsProximityMeters: number | null;
  checkoutGpsProximityEnabled: boolean;
  checkoutGpsProximityMeters: number | null;
  checkInStaffSignatureRequired: boolean;
  checkoutStaffSignatureRequired: boolean;
  interpreterCollectCheckInClientSignatureEnabled: boolean;
  interpreterCollectCheckoutClientSignatureEnabled: boolean;
  checkInClientSignatureGpsProximityEnabled: boolean;
  checkInClientSignatureGpsProximityMeters: number | null;
  checkoutClientSignatureGpsProximityEnabled: boolean;
  checkoutClientSignatureGpsProximityMeters: number | null;
  staffMemberEmailInputEnabled: boolean;
  additionalParticipantsForApprovalEnabled: boolean;
  staffEmailDomainRestrictionEnabled: boolean;
  allowedStaffEmailDomains?: AllowedStaffEmailDomain[];
  additionalParticipants?: VosApprovalParticipant[];
  digitalVosEnabled: boolean;
}

function defaultDto(): VosAttendanceSettingsDto {
  return {
    checkInGpsProximityEnabled: false,
    checkInGpsProximityMeters: null,
    checkoutGpsProximityEnabled: false,
    checkoutGpsProximityMeters: null,
    checkInStaffSignatureRequired: false,
    checkoutStaffSignatureRequired: false,
    interpreterCollectCheckInClientSignatureEnabled: false,
    interpreterCollectCheckoutClientSignatureEnabled: false,
    checkInClientSignatureGpsProximityEnabled: false,
    checkInClientSignatureGpsProximityMeters: null,
    checkoutClientSignatureGpsProximityEnabled: false,
    checkoutClientSignatureGpsProximityMeters: null,
    staffMemberEmailInputEnabled: true,
    additionalParticipantsForApprovalEnabled: false,
    staffEmailDomainRestrictionEnabled: false,
    allowedStaffEmailDomains: [],
    additionalParticipants: [],
    digitalVosEnabled: false,
  };
}

function toParticipantOption(p: VosApprovalParticipant): ParticipantOption {
  const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email || p.userId;
  return {
    value: p.userId,
    label: p.email ? `${name} (${p.email})` : name,
    email: p.email ?? '',
  };
}

const participantSelectStyles: StylesConfig<ParticipantOption, true> = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function inputImpliesSubdomains(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed.startsWith('*.') || trimmed.toLowerCase().startsWith('@*.');
}

function normalizeDomainInput(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  let withoutPrefix = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  if (withoutPrefix.startsWith('*.')) withoutPrefix = withoutPrefix.slice(2);
  const domain = withoutPrefix.replace(/\.+$/, '').trim();
  if (!domain || domain.includes('@') || domain.includes(' ') || domain.includes('/')) return null;
  return domain;
}

function normalizeDomainRulesFromApi(
  rules: Array<AllowedStaffEmailDomain | string> | undefined,
): AllowedStaffEmailDomain[] {
  if (!rules?.length) return [];

  return rules
    .map((entry) => {
      if (typeof entry === 'string') {
        const domain = normalizeDomainInput(entry);
        return domain ? { domain, allowSubdomains: inputImpliesSubdomains(entry) } : null;
      }

      const domain = normalizeDomainInput(entry.domain);
      if (!domain) return null;
      return {
        domain,
        allowSubdomains: entry.allowSubdomains || inputImpliesSubdomains(entry.domain),
      };
    })
    .filter((entry): entry is AllowedStaffEmailDomain => entry != null);
}

function formatDomainLabel(rule: AllowedStaffEmailDomain): string {
  return rule.allowSubdomains ? `@*.${rule.domain}` : `@${rule.domain}`;
}

interface SettingsSectionProps {
  title: string;
  description: string;
  iconClass: string;
  children: React.ReactNode;
}

function SettingsSection({ title, description, iconClass, children }: SettingsSectionProps) {
  return (
    <div className="ipi-settings-section p-4 p-md-5">
      <div className="row g-4">
        <div className="col-md-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className={`bi ${iconClass} text-muted fs-5`} aria-hidden="true" />
            <h5 className="fw-bold text-gray-900 mb-0">{title}</h5>
          </div>
          <p className="text-muted small mb-0 lh-base">{description}</p>
        </div>
        <div className="col-md-8 d-flex flex-column gap-3">{children}</div>
      </div>
    </div>
  );
}

interface SettingRowProps {
  label: string;
  description?: string;
  control: React.ReactNode;
  children?: React.ReactNode;
}

function SettingRow({ label, description, control, children }: SettingRowProps) {
  return (
    <div className="ipi-setting-row d-flex flex-column flex-sm-row align-items-sm-start justify-content-between gap-3 p-3 rounded border border-gray-200 bg-light">
      <div className="flex-grow-1">
        <div className="fw-semibold text-gray-900">{label}</div>
        {description && <div className="text-muted small mt-1">{description}</div>}
        {children && <div className="mt-3">{children}</div>}
      </div>
      <div className="flex-shrink-0">{control}</div>
    </div>
  );
}

export function InPersonInterpretationSettingsForm({ customerId }: { customerId: number }) {
  const [ipiSubTab, setIpiSubTab] = useState<'mobile' | 'bulk'>('mobile')
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<VosAttendanceSettingsDto>(defaultDto);
  const [domainInput, setDomainInput] = useState('');
  const [allowSubdomainsInput, setAllowSubdomainsInput] = useState(false);
  const [domainInputError, setDomainInputError] = useState<string | null>(null);
  const [participantOptions, setParticipantOptions] = useState<ParticipantOption[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  const selectedParticipantOptions = useMemo(
    () => (form.additionalParticipants ?? []).map(toParticipantOption),
    [form.additionalParticipants],
  );

  const load = useCallback(async () => {
    if (!customerId || customerId <= 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get<VosAttendanceSettingsDto>(
        `${API_URL}/CustomerSettings/mobile-attendance/${customerId}`
      );
      const additionalParticipants = Array.isArray(res.data.additionalParticipants)
        ? res.data.additionalParticipants
        : [];
      setForm({
        ...defaultDto(),
        ...res.data,
        staffMemberEmailInputEnabled: res.data.staffMemberEmailInputEnabled ?? true,
        additionalParticipantsForApprovalEnabled:
          res.data.additionalParticipantsForApprovalEnabled ?? false,
        staffEmailDomainRestrictionEnabled: res.data.staffEmailDomainRestrictionEnabled ?? false,
        digitalVosEnabled: res.data.digitalVosEnabled ?? false,
        allowedStaffEmailDomains: normalizeDomainRulesFromApi(res.data.allowedStaffEmailDomains),
        additionalParticipants,
      });
      setParticipantOptions(additionalParticipants.map(toParticipantOption));
    } catch (err: any) {
      toaster('error', err?.response?.data?.message ?? 'Failed to load mobile attendance settings');
      setForm(defaultDto());
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const update =
    (patch: Partial<VosAttendanceSettingsDto>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const searchEligibleParticipants = useCallback(
    async (search: string) => {
      if (!customerId || customerId <= 0) return;
      setParticipantsLoading(true);
      try {
        const res = await axios.get<VosApprovalParticipant[]>(
          `${API_URL}/CustomerSettings/mobile-attendance/${customerId}/eligible-participants`,
          { params: { search: search || undefined, limit: 50 } }
        );
        const results = Array.isArray(res.data) ? res.data.map(toParticipantOption) : [];
        const selectedIds = new Set((form.additionalParticipants ?? []).map((p) => p.userId));
        const merged = [...(form.additionalParticipants ?? []).map(toParticipantOption)];
        for (const opt of results) {
          if (!selectedIds.has(opt.value) && !merged.some((m) => m.value === opt.value)) {
            merged.push(opt);
          }
        }
        setParticipantOptions(merged);
      } catch {
        // Keep existing options; search failures should not clear selection.
      } finally {
        setParticipantsLoading(false);
      }
    },
    [customerId, form.additionalParticipants],
  );

  useEffect(() => {
    if (!form.additionalParticipantsForApprovalEnabled || !customerId) return;
    void searchEligibleParticipants('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.additionalParticipantsForApprovalEnabled, customerId]);

  const onParticipantsChange = (options: MultiValue<ParticipantOption>) => {
    const byId = new Map((form.additionalParticipants ?? []).map((p) => [p.userId, p]));
    const next: VosApprovalParticipant[] = options.map((o) => {
      const existing = byId.get(o.value);
      if (existing) return existing;
      const nameWithoutEmail = o.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const parts = nameWithoutEmail.split(/\s+/).filter(Boolean);
      return {
        userId: o.value,
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' '),
        email: o.email,
      };
    });
    update({ additionalParticipants: next });
  };

  const addDomain = () => {
    const normalized = normalizeDomainInput(domainInput);
    if (!normalized) {
      setDomainInputError('Enter a valid domain (e.g. nih.us.com, @nih.us.com, or *.health.org).');
      return;
    }

    const allowSubdomains = allowSubdomainsInput || inputImpliesSubdomains(domainInput);
    const existing = form.allowedStaffEmailDomains ?? [];
    const duplicate = existing.find((entry) => entry.domain === normalized);
    if (duplicate) {
      setDomainInputError('This domain is already in the list.');
      return;
    }

    setForm((prev) => ({
      ...prev,
      allowedStaffEmailDomains: [
        ...(prev.allowedStaffEmailDomains ?? []),
        { domain: normalized, allowSubdomains },
      ],
    }));
    setDomainInput('');
    setAllowSubdomainsInput(false);
    setDomainInputError(null);
  };

  const removeDomain = (domain: string) => {
    setForm((prev) => ({
      ...prev,
      allowedStaffEmailDomains: (prev.allowedStaffEmailDomains ?? []).filter((entry) => entry.domain !== domain),
    }));
  };

  const toggleDomainSubdomains = (domain: string, allowSubdomains: boolean) => {
    setForm((prev) => ({
      ...prev,
      allowedStaffEmailDomains: (prev.allowedStaffEmailDomains ?? []).map((entry) =>
        entry.domain === domain ? { ...entry, allowSubdomains } : entry,
      ),
    }));
  };

  const save = async () => {
    if (!customerId || customerId <= 0) return;

    if (form.checkInGpsProximityEnabled) {
      const m = form.checkInGpsProximityMeters;
      if (m == null || Number.isNaN(m) || m < 1) {
        toaster('error', 'Check-in GPS proximity requires a distance of at least 1 meter.');
        return;
      }
    }
    if (form.checkoutGpsProximityEnabled) {
      const m = form.checkoutGpsProximityMeters;
      if (m == null || Number.isNaN(m) || m < 1) {
        toaster('error', 'Check-out GPS proximity requires a distance of at least 1 meter.');
        return;
      }
    }
    const checkInClientSigGpsEnabled =
      form.interpreterCollectCheckInClientSignatureEnabled && form.checkInClientSignatureGpsProximityEnabled;
    const checkoutClientSigGpsEnabled =
      form.interpreterCollectCheckoutClientSignatureEnabled && form.checkoutClientSignatureGpsProximityEnabled;

    if (checkInClientSigGpsEnabled) {
      const m = form.checkInClientSignatureGpsProximityMeters;
      if (m == null || Number.isNaN(m) || m < 1) {
        toaster('error', 'Check-in client signature GPS proximity requires a distance of at least 1 meter.');
        return;
      }
    }
    if (checkoutClientSigGpsEnabled) {
      const m = form.checkoutClientSignatureGpsProximityMeters;
      if (m == null || Number.isNaN(m) || m < 1) {
        toaster('error', 'Check-out client signature GPS proximity requires a distance of at least 1 meter.');
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/CustomerSettings/mobile-attendance/${customerId}`, {
        checkInGpsProximityEnabled: form.checkInGpsProximityEnabled,
        checkInGpsProximityMeters: form.checkInGpsProximityEnabled ? form.checkInGpsProximityMeters : null,
        checkoutGpsProximityEnabled: form.checkoutGpsProximityEnabled,
        checkoutGpsProximityMeters: form.checkoutGpsProximityEnabled ? form.checkoutGpsProximityMeters : null,
        checkInStaffSignatureRequired: form.checkInStaffSignatureRequired,
        checkoutStaffSignatureRequired: form.checkoutStaffSignatureRequired,
        interpreterCollectCheckInClientSignatureEnabled: form.interpreterCollectCheckInClientSignatureEnabled,
        interpreterCollectCheckoutClientSignatureEnabled: form.interpreterCollectCheckoutClientSignatureEnabled,
        checkInClientSignatureGpsProximityEnabled: checkInClientSigGpsEnabled,
        checkInClientSignatureGpsProximityMeters: checkInClientSigGpsEnabled
          ? form.checkInClientSignatureGpsProximityMeters
          : null,
        checkoutClientSignatureGpsProximityEnabled: checkoutClientSigGpsEnabled,
        checkoutClientSignatureGpsProximityMeters: checkoutClientSigGpsEnabled
          ? form.checkoutClientSignatureGpsProximityMeters
          : null,
        staffMemberEmailInputEnabled: form.staffMemberEmailInputEnabled,
        additionalParticipantsForApprovalEnabled: form.additionalParticipantsForApprovalEnabled,
        staffEmailDomainRestrictionEnabled: form.staffEmailDomainRestrictionEnabled,
        allowedStaffEmailDomains: form.allowedStaffEmailDomains ?? [],
        additionalParticipantUserIds: (form.additionalParticipants ?? []).map((p) => p.userId),
        digitalVosEnabled: form.digitalVosEnabled,
      });
      toaster('success', 'Mobile attendance settings saved');
      await load();
    } catch (err: any) {
      toaster('error', err?.response?.data?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <Spinner animation="border" className="text-gray-500" />
      </div>
    );
  }

  if (ipiSubTab === 'bulk') {
    return (
      <div className="vis-mr-root bulk-vos-settings-page">
        <div className="bulk-vos-settings-card">
          <div className="bulk-vos-settings-subtabs">
            <button
              type="button"
              className="bulk-vos-settings-subtab"
              onClick={() => setIpiSubTab('mobile')}
            >
              Mobile App
            </button>
            <button
              type="button"
              className="bulk-vos-settings-subtab bulk-vos-settings-subtab--active"
              onClick={() => setIpiSubTab('bulk')}
            >
              Bulk VOS
            </button>
          </div>
          <BulkVosSettingsTab customerId={customerId} />
        </div>
      </div>
    )
  }

  return (
    <div className="vis-mr-root bulk-vos-settings-page">
      <div className="bulk-vos-settings-card">
        <div className="bulk-vos-settings-subtabs">
          <button
            type="button"
            className="bulk-vos-settings-subtab bulk-vos-settings-subtab--active"
            onClick={() => setIpiSubTab('mobile')}
          >
            Mobile App
          </button>
          <button
            type="button"
            className="bulk-vos-settings-subtab"
            onClick={() => setIpiSubTab('bulk')}
          >
            Bulk VOS
          </button>
        </div>
        <div className="bulk-vos-settings-inner">
      <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 pb-4 mb-4 border-bottom border-gray-200">
        <div>
          <h4 className="fw-bold text-gray-900 mb-1">Mobile App Settings</h4>
          <p className="text-muted mb-0">
            Configure mobile app behaviour for interpreters on behalf of this client.
          </p>
        </div>
        <Button
          className="vis-mr-btn-primary px-4 py-2 fw-semibold flex-shrink-0"
          onClick={save}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>

      <div className="card border border-gray-300 overflow-hidden ipi-settings-card">
        <SettingsSection
          title="Digital VOS"
          description="Control whether interpreters can use Digital VOS in the mobile app for this client."
          iconClass="bi-file-earmark-text"
        >
          <SettingRow
            label="Enable Digital VOS"
            description="When off, interpreters cannot submit Digital VOS from the mobile app for this client."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-digital-vos-enabled"
                className="vis-mr-toggle m-0"
                checked={form.digitalVosEnabled}
                onChange={(e) => update({ digitalVosEnabled: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
        </SettingsSection>

        <hr className="m-0 border-gray-200" />

        <SettingsSection
          title="Location Tracking"
          description="Enforce geographical boundaries for checking in and out of appointments."
          iconClass="bi-geo-alt"
        >
          <SettingRow
            label="Require GPS proximity for check-in"
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkin-gps"
                className="vis-mr-toggle m-0"
                checked={form.checkInGpsProximityEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    checkInGpsProximityEnabled: on,
                    checkInGpsProximityMeters: on ? form.checkInGpsProximityMeters ?? 100 : null,
                  });
                }}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          >
            {form.checkInGpsProximityEnabled && (
              <div className="d-flex align-items-center gap-2" style={{ maxWidth: 220 }}>
                <Form.Label className="fw-semibold fs-7 mb-0 text-nowrap">Radius limit</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={form.checkInGpsProximityMeters ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    update({ checkInGpsProximityMeters: v });
                  }}
                  className="form-control-sm"
                />
                <span className="text-muted small">m</span>
              </div>
            )}
          </SettingRow>

          <SettingRow
            label="Require GPS proximity for check-out"
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkout-gps"
                className="vis-mr-toggle m-0"
                checked={form.checkoutGpsProximityEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    checkoutGpsProximityEnabled: on,
                    checkoutGpsProximityMeters: on ? form.checkoutGpsProximityMeters ?? 100 : null,
                  });
                }}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          >
            {form.checkoutGpsProximityEnabled && (
              <div className="d-flex align-items-center gap-2" style={{ maxWidth: 220 }}>
                <Form.Label className="fw-semibold fs-7 mb-0 text-nowrap">Radius limit</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={form.checkoutGpsProximityMeters ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    update({ checkoutGpsProximityMeters: v });
                  }}
                  className="form-control-sm"
                />
                <span className="text-muted small">m</span>
              </div>
            )}
          </SettingRow>

          <SettingRow
            label="Require GPS proximity for check-in client signature"
            description={
              form.interpreterCollectCheckInClientSignatureEnabled
                ? 'Interpreter must be within the radius to collect the client check-in signature on their device.'
                : 'Enable “Allow interpreter to collect check-in signature” first.'
            }
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkin-client-sig-gps"
                className="vis-mr-toggle m-0"
                disabled={!form.interpreterCollectCheckInClientSignatureEnabled}
                checked={
                  form.interpreterCollectCheckInClientSignatureEnabled &&
                  form.checkInClientSignatureGpsProximityEnabled
                }
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    checkInClientSignatureGpsProximityEnabled: on,
                    checkInClientSignatureGpsProximityMeters: on
                      ? form.checkInClientSignatureGpsProximityMeters ?? 100
                      : null,
                  });
                }}
                style={{
                  width: '2.75rem',
                  height: '1.35rem',
                  cursor: form.interpreterCollectCheckInClientSignatureEnabled ? 'pointer' : 'not-allowed',
                }}
              />
            }
          >
            {form.interpreterCollectCheckInClientSignatureEnabled &&
              form.checkInClientSignatureGpsProximityEnabled && (
              <div className="d-flex align-items-center gap-2" style={{ maxWidth: 220 }}>
                <Form.Label className="fw-semibold fs-7 mb-0 text-nowrap">Radius limit</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={form.checkInClientSignatureGpsProximityMeters ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    update({ checkInClientSignatureGpsProximityMeters: v });
                  }}
                  className="form-control-sm"
                />
                <span className="text-muted small">m</span>
              </div>
            )}
          </SettingRow>

          <SettingRow
            label="Require GPS proximity for check-out client signature"
            description={
              form.interpreterCollectCheckoutClientSignatureEnabled
                ? 'Interpreter must be within the radius to collect the client check-out signature on their device.'
                : 'Enable “Allow interpreter to collect check-out signature” first.'
            }
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkout-client-sig-gps"
                className="vis-mr-toggle m-0"
                disabled={!form.interpreterCollectCheckoutClientSignatureEnabled}
                checked={
                  form.interpreterCollectCheckoutClientSignatureEnabled &&
                  form.checkoutClientSignatureGpsProximityEnabled
                }
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    checkoutClientSignatureGpsProximityEnabled: on,
                    checkoutClientSignatureGpsProximityMeters: on
                      ? form.checkoutClientSignatureGpsProximityMeters ?? 100
                      : null,
                  });
                }}
                style={{
                  width: '2.75rem',
                  height: '1.35rem',
                  cursor: form.interpreterCollectCheckoutClientSignatureEnabled ? 'pointer' : 'not-allowed',
                }}
              />
            }
          >
            {form.interpreterCollectCheckoutClientSignatureEnabled &&
              form.checkoutClientSignatureGpsProximityEnabled && (
              <div className="d-flex align-items-center gap-2" style={{ maxWidth: 220 }}>
                <Form.Label className="fw-semibold fs-7 mb-0 text-nowrap">Radius limit</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={form.checkoutClientSignatureGpsProximityMeters ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    update({ checkoutClientSignatureGpsProximityMeters: v });
                  }}
                  className="form-control-sm"
                />
                <span className="text-muted small">m</span>
              </div>
            )}
          </SettingRow>
        </SettingsSection>

        <hr className="m-0 border-gray-200" />

        <SettingsSection
          title="Signature Permissions"
          description="Require staff members or requesters to sign forms before completing actions."
          iconClass="bi-pen"
        >
          <SettingRow
            label="Require signature for check-in"
            description="Check-in only completes after the staff member or requester signs the form."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkin-sig"
                className="vis-mr-toggle m-0"
                checked={form.checkInStaffSignatureRequired}
                onChange={(e) => update({ checkInStaffSignatureRequired: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Require signature for check-out"
            description="Check-out only completes after the staff member or requester signs the form."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-checkout-sig"
                className="vis-mr-toggle m-0"
                checked={form.checkoutStaffSignatureRequired}
                onChange={(e) => update({ checkoutStaffSignatureRequired: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Allow interpreter to collect check-in signature"
            description="Interpreters can hand their device to the client to capture the check-in signature in the mobile app. Email and requester-app approval remain available."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-interpreter-collect-checkin-sig"
                className="vis-mr-toggle m-0"
                checked={form.interpreterCollectCheckInClientSignatureEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    interpreterCollectCheckInClientSignatureEnabled: on,
                    ...(on
                      ? {}
                      : {
                          checkInClientSignatureGpsProximityEnabled: false,
                          checkInClientSignatureGpsProximityMeters: null,
                        }),
                  });
                }}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Allow interpreter to collect check-out signature"
            description="Interpreters can hand their device to the client to capture the check-out / VOS signature in the mobile app. Email and requester-app approval remain available."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-interpreter-collect-checkout-sig"
                className="vis-mr-toggle m-0"
                checked={form.interpreterCollectCheckoutClientSignatureEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  update({
                    interpreterCollectCheckoutClientSignatureEnabled: on,
                    ...(on
                      ? {}
                      : {
                          checkoutClientSignatureGpsProximityEnabled: false,
                          checkoutClientSignatureGpsProximityMeters: null,
                        }),
                  });
                }}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
          <SettingRow
            label="Require Staff Member Email"
            description="Ask interpreters to input the staff member's name and email on the app."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-staff-email-input"
                className="vis-mr-toggle m-0"
                checked={form.staffMemberEmailInputEnabled}
                onChange={(e) => update({ staffMemberEmailInputEnabled: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          />
        </SettingsSection>

        <hr className="m-0 border-gray-200" />

        <SettingsSection
          title="Approvals & Access"
          description="Configure who receives confirmation emails and restrict valid staff email domains."
          iconClass="bi-globe"
        >
          <SettingRow
            label="Include additional participants in approvals"
            description="Selected users receive VOS approval emails alongside the requester."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-additional-participants"
                className="vis-mr-toggle m-0"
                checked={form.additionalParticipantsForApprovalEnabled}
                onChange={(e) => update({ additionalParticipantsForApprovalEnabled: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          >
            {form.additionalParticipantsForApprovalEnabled && (
              <div>
                <Form.Label className="fw-semibold fs-7">Participants</Form.Label>
                <Select
                  isMulti
                  isClearable
                  isSearchable
                  isLoading={participantsLoading}
                  options={participantOptions}
                  value={selectedParticipantOptions}
                  onChange={onParticipantsChange}
                  onInputChange={(value: string, meta: InputActionMeta) => {
                    if (meta.action === 'input-change') {
                      void searchEligibleParticipants(value);
                    }
                    return value;
                  }}
                  onMenuOpen={() => {
                    if (participantOptions.length === 0) void searchEligibleParticipants('');
                  }}
                  placeholder="Search client users by name or email…"
                  noOptionsMessage={() => (participantsLoading ? 'Loading…' : 'No client users found')}
                  classNamePrefix="react-select"
                  className="react-select-styled react-select-solid react-select-sm"
                  styles={participantSelectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
                <Form.Text className="text-muted">
                  Only active client users for this account can be selected. Invalid or removed users are skipped at send
                  time.
                </Form.Text>
              </div>
            )}
          </SettingRow>

          <SettingRow
            label="Restrict staff emails by domain"
            description="If no emails are configured or toggled off then emails for all domains will be allowed."
            control={
              <Form.Check
                type="switch"
                role="switch"
                id="ipi-domain-restriction"
                className="vis-mr-toggle m-0"
                checked={form.staffEmailDomainRestrictionEnabled}
                onChange={(e) => update({ staffEmailDomainRestrictionEnabled: e.target.checked })}
                style={{ width: '2.75rem', height: '1.35rem', cursor: 'pointer' }}
              />
            }
          >
            {form.staffEmailDomainRestrictionEnabled && (
              <div>
                <Form.Label className="fw-semibold fs-7 mb-1">Allowed Email Domains</Form.Label>
                <p className="text-muted small mb-3">
                  Restrict staff member emails to specific organization domains. Subdomain restriction can be enabled per
                  domain.
                </p>

                <div className="d-flex flex-column gap-2 mb-3">
                  {(form.allowedStaffEmailDomains ?? []).length === 0 ? (
                    <div className="border border-dashed rounded px-3 py-4 text-center text-muted small bg-white">
                      No domains configured. Emails from all domains are currently allowed.
                    </div>
                  ) : (
                    (form.allowedStaffEmailDomains ?? []).map((rule) => (
                      <div
                        key={rule.domain}
                        className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 border rounded px-3 py-2 bg-white"
                      >
                        <div>
                          <span className="fw-semibold text-gray-800">{formatDomainLabel(rule)}</span>
                          <Form.Check
                            type="switch"
                            id={`allow-subdomains-${rule.domain}`}
                            label="Allow all subdomains (@*.domain.com)"
                            checked={rule.allowSubdomains}
                            onChange={(e) => toggleDomainSubdomains(rule.domain, e.target.checked)}
                            className="mb-0 small mt-2"
                          />
                        </div>
                        <button
                          type="button"
                          className="ipi-domain-remove-btn"
                          title={`Remove ${rule.domain}`}
                          aria-label={`Remove ${rule.domain}`}
                          onClick={() => removeDomain(rule.domain)}
                        >
                          <i className="bi bi-trash fs-6" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="d-flex flex-column flex-md-row gap-2" style={{ maxWidth: 560 }}>
                  <Form.Control
                    type="text"
                    placeholder="e.g. nih.us.com or *.health.org"
                    value={domainInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDomainInput(value);
                      if (inputImpliesSubdomains(value)) setAllowSubdomainsInput(true);
                      if (domainInputError) setDomainInputError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addDomain();
                      }
                    }}
                    className="form-control-sm"
                    isInvalid={!!domainInputError}
                  />
                  <Button variant="outline-secondary" size="sm" className="px-3 py-1 text-nowrap" onClick={addDomain}>
                    + Add domain
                  </Button>
                </div>
                {domainInputError && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {domainInputError}
                  </Form.Control.Feedback>
                )}
              </div>
            )}
          </SettingRow>
        </SettingsSection>
      </div>
        </div>
      </div>
    </div>
  );
}
